/**
 * MetricsSummaryCard Component
 * Displays detailed metrics with YoY and MoM comparisons
 */
import { ArrowDownRight, ArrowUpRight, Info, LucideIcon } from "lucide-react";

import { useSettingsStore } from "../../../stores/settingsStore";

export interface MetricItem {
  icon: LucideIcon;
  label: string;
  value: string | number;
  yoyChange: string;
  momChange: string;
  yoyType: "increase" | "decrease";
  momType: "increase" | "decrease";
  /** Mark as true for cost-based KPIs (Cost, CPC, CPA, ROAS, revenue) */
  isCostMetric?: boolean;
  availabilityStatus?:
    | "available"
    | "measured_zero"
    | "not_collected"
    | "unsupported"
    | "sync_error"
    | "partial";
  availabilityLabel?: string;
}

interface MetricsSummaryCardProps {
  title: string;
  titleIcon: LucideIcon;
  accentColor: "primary" | "secondary";
  metrics: MetricItem[];
  /** When true, shows a ⓘ tooltip on cost-based metrics indicating fee is included */
  feeIncluded?: boolean;
  /** Tooltip message — defaults to 'Cost includes agency fee' */
  feeTooltipLabel?: string;
}

/** Condenses large percentage strings like "+4108.8%" → "+4.1K%" */
function compactChange(raw: string): { display: string; full: string } {
  const match = /^([+-]?)(\d+(?:\.\d+)?)(%.*)$/.exec(raw);
  if (!match) return { display: raw, full: raw };
  const sign = match[1] ?? "";
  const numStr = match[2] ?? "";
  const suffix = match[3] ?? "";
  const num = parseFloat(numStr);
  if (num >= 1_000_000) {
    const c = (num / 1_000_000).toFixed(1).replace(/\.0$/, "");
    return { display: `${sign}${c}M${suffix}`, full: raw };
  }
  if (num >= 1_000) {
    const c = (num / 1_000).toFixed(1).replace(/\.0$/, "");
    return { display: `${sign}${c}K${suffix}`, full: raw };
  }
  return { display: raw, full: raw };
}

export default function MetricsSummaryCard({
  title,
  titleIcon: TitleIcon,
  accentColor,
  metrics,
  feeIncluded = false,
  feeTooltipLabel = "Cost includes agency fee",
}: MetricsSummaryCardProps) {
  const darkMode = useSettingsStore((state) => state.darkMode);

  const brandColor =
    accentColor === "primary"
      ? "var(--brand-primary)"
      : "var(--brand-secondary)";

  return (
    <div
      className={`group relative rounded-3xl p-4 md:p-5 lg:p-6 border shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden ${
        darkMode
          ? `bg-gradient-to-br from-gray-800 via-gray-800/95 to-gray-800/80 border-gray-700 hover:border-[${brandColor}]/50`
          : `bg-gradient-to-br from-white via-white to-gray-50/50 border-gray-200 hover:border-[${brandColor}]/30 hover:shadow-[${brandColor}]/10`
      }`}
    >
      {/* Top accent border */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[${brandColor}] to-[${brandColor}]/50 rounded-t-3xl`}
        style={{
          background: `linear-gradient(to right, ${brandColor}, ${brandColor}80)`,
        }}
      ></div>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 md:p-2.5 lg:p-3 rounded-2xl shadow-lg transition-transform group-hover:scale-110 duration-300`}
            style={{
              background: darkMode
                ? `linear-gradient(to bottom right, ${brandColor}4D, ${brandColor}1A)`
                : `linear-gradient(to bottom right, ${brandColor}33, ${brandColor}0D)`,
            }}
          >
            <TitleIcon
              className="w-4 h-4 md:w-5 md:h-5 lg:w-6 lg:h-6"
              style={{ color: brandColor }}
            />
          </div>
          <div>
            <h4
              className={`text-sm font-bold tracking-wide ${
                darkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {title}
            </h4>
          </div>
        </div>
      </div>

      <div className="space-y-5">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          const isLast = index === metrics.length - 1;
          const yoy = compactChange(metric.yoyChange);
          const mom = compactChange(metric.momChange);

          return (
            <div
              key={index}
              className={
                !isLast
                  ? `pb-5 border-b ${darkMode ? "border-gray-700/50" : "border-gray-200"}`
                  : ""
              }
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" style={{ color: brandColor }} />
                <p
                  className={`text-xs font-medium uppercase tracking-wide ${darkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  {metric.label}
                </p>
                {metric.availabilityStatus && metric.availabilityLabel && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[9px] font-semibold normal-case tracking-normal ${
                      metric.availabilityStatus === "available"
                        ? darkMode
                          ? "bg-green-500/20 text-green-300"
                          : "bg-green-50 text-green-700"
                        : metric.availabilityStatus === "measured_zero" ||
                            metric.availabilityStatus === "partial"
                          ? darkMode
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-amber-50 text-amber-700"
                          : metric.availabilityStatus === "sync_error"
                            ? darkMode
                              ? "bg-red-500/20 text-red-300"
                              : "bg-red-50 text-red-700"
                            : darkMode
                              ? "bg-gray-700 text-gray-300"
                              : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {metric.availabilityLabel}
                  </span>
                )}
                {feeIncluded && metric.isCostMetric && (
                  <span
                    data-tooltip-id="fee-kpi-tooltip"
                    data-tooltip-content={feeTooltipLabel}
                    className="inline-flex items-center cursor-help"
                    aria-label={feeTooltipLabel}
                  >
                    <Info
                      className={`w-3.5 h-3.5 ${darkMode ? "text-[var(--brand-secondary)]" : "text-[var(--brand-secondary)]"}`}
                    />
                  </span>
                )}
              </div>
              <p
                className={`text-2xl md:text-3xl lg:text-4xl font-bold mb-3 ${darkMode ? "text-white" : "text-gray-900"}`}
              >
                {metric.value}
              </p>
              <div className="flex items-center gap-1 lg:gap-2 flex-nowrap">
                {/* YoY Change */}
                <div
                  className={`flex items-center gap-1 px-1.5 md:px-2 lg:px-2.5 py-0.5 md:py-1 rounded-lg ${
                    metric.yoyType === "increase"
                      ? darkMode
                        ? "bg-green-500/20"
                        : "bg-green-50"
                      : darkMode
                        ? "bg-red-500/20"
                        : "bg-red-50"
                  }`}
                >
                  {metric.yoyType === "increase" ? (
                    <ArrowUpRight className="w-3 h-3 text-green-600 hidden md:block lg:block" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 text-red-600 hidden md:block lg:block" />
                  )}
                  <span
                    className={`text-[10px] md:text-[10px] lg:text-xs font-semibold ${metric.yoyType === "increase" ? "text-green-600" : "text-red-600"}`}
                    title={yoy.display !== yoy.full ? yoy.full : undefined}
                  >
                    {yoy.display}
                  </span>
                  <span
                    className={`text-[8px] font-bold ${darkMode ? "text-gray-400" : "text-gray-500"} hidden lg:inline`}
                  >
                    YoY
                  </span>
                </div>
                {/* MoM Change */}
                <div
                  className={`flex items-center gap-1 px-1.5 md:px-2 lg:px-2.5 py-0.5 md:py-1 rounded-lg ${
                    metric.momType === "increase"
                      ? darkMode
                        ? "bg-green-500/20"
                        : "bg-green-50"
                      : darkMode
                        ? "bg-red-500/20"
                        : "bg-red-50"
                  }`}
                >
                  {metric.momType === "increase" ? (
                    <ArrowUpRight className="w-3 h-3 text-green-600 hidden md:block lg:block" />
                  ) : (
                    <ArrowDownRight className="w-3 h-3 text-red-600 hidden md:block lg:block" />
                  )}
                  <span
                    className={`text-[10px] md:text-[10px] lg:text-xs font-semibold ${metric.momType === "increase" ? "text-green-600" : "text-red-600"}`}
                    title={mom.display !== mom.full ? mom.full : undefined}
                  >
                    {mom.display}
                  </span>
                  <span
                    className={`text-[8px] font-bold ${darkMode ? "text-gray-400" : "text-gray-500"} hidden lg:inline`}
                  >
                    MoM
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
